import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import Section from 'core/components/section';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import OnboardingTasks from './components/onboarding-tasks';
import { tasksThatCanBeUploadedByExternalAgent } from './components/helpers/task-helpers';
import { useOfferTasks } from './components/hooks/use-offer-tasks';
import helpCircleIcon from 'core/assets/svg/help-circle.svg';
import coffeeIcon from 'customer/components/onboarding-task-center/assets/coffee.svg';
import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import useStyles from './components/onboarding-tasks.styles';

/**
 * Offer Onboarding Tasks Component
 * Displays and manages onboarding tasks for an offer (pre-purchase)
 *
 * @param {Object} props
 * @param {string} props.offerId - The offer ID
 */
const OfferOnboardingTasks = ({
  offerId,
  webUserId,
  // When these props are provided, use them instead of calling useOfferTasks internally.
  // This allows a parent component to share the same tasks state (e.g. CheckoutForm
  // shares tasks between PreSaleChecklist and OfferOnboardingTasks).
  tasks: tasksProp,
  incompleteTasks: incompleteTasksProp,
  completedTasks: completedTasksProp,
  tasksLoading: loadingProp,
  tasksError: errorProp,
  uploadDocument: uploadDocumentProp,
  deleteDocument: deleteDocumentProp,
  onTasksChange
}) => {
  const classes = useStyles();
  const { canUploadTaskCenterDocuments, isAgency, user } = useSession();
  const { offer } = useStore();

  // Use props if provided (shared state from parent), otherwise use own hook instance
  const hasSharedState = tasksProp !== undefined;
  const hookResult = useOfferTasks(
    hasSharedState ? null : offerId, // pass null to skip fetching when using shared state
    hasSharedState ? null : webUserId,
    hasSharedState ? null : user.username
  );

  const tasks = hasSharedState ? tasksProp : hookResult.tasks;
  const incompleteTasks = hasSharedState ? incompleteTasksProp : hookResult.incompleteTasks;
  const completedTasks = hasSharedState ? completedTasksProp : hookResult.completedTasks;
  const loading = hasSharedState ? loadingProp : hookResult.loading;
  const error = hasSharedState ? errorProp : hookResult.error;
  const uploadDocFn = hasSharedState ? uploadDocumentProp : hookResult.uploadDocument;
  const deleteDocFn = hasSharedState ? deleteDocumentProp : hookResult.deleteDocument;

  const handleUpload = async ({ file, taskName, taskId, onProgress }) => {
    await uploadDocFn({ file, taskName, taskId, onProgress });
    if (onTasksChange) {
      onTasksChange();
    }
  };

  const handleDownload = async (path) => {
    // Use the store's downloadDocument method to get a signed URL
    await offer.downloadDocument(path);
  };

  const handleDelete = async (file) => {
    await deleteDocFn(file);
    if (onTasksChange) {
      onTasksChange();
    }
  };

  return (
    <Section
      title="Post-purchase tasks"
      subtitle="- Optional"
      rightLabel="Total"
      rightValue={tasks.length ? `${loading ? '...' : incompleteTasks.length} incomplete tasks` : null}
      titleTooltipText="Get ahead of post-purchase tasks by uploading any required documents now. This helps confirm discounts, prior coverage, and eligibility requirements upfront—giving you and the member fewer things to do after purchase."
      titleTooltipIcon={helpCircleIcon}
    >
      {!loading && tasks.length === 0 ? (
        <Card>
          <div className={classes.centerContainer}>
            <img src={coffeeIcon} alt="Coffee Icon" />
            <Label type="darkGreenSmall">There are no tasks that can be completed early.</Label>
          </div>
        </Card>
      ) : (
        <OnboardingTasks
          offerId={offerId}
          tasks={tasks}
          incompleteTasks={incompleteTasks}
          completedTasks={completedTasks}
          loading={loading}
          error={error}
          onUploadDocument={handleUpload}
          onDownloadDocument={handleDownload}
          onDeleteDocument={handleDelete}
          canUploadDocuments={canUploadTaskCenterDocuments}
          isAgency={isAgency}
          allowedTasksForUpload={tasksThatCanBeUploadedByExternalAgent}
        />
      )}
    </Section>
  );
};

OfferOnboardingTasks.propTypes = {
  offerId: PropTypes.string.isRequired,
  webUserId: PropTypes.string.isRequired,
  tasks: PropTypes.array,
  incompleteTasks: PropTypes.array,
  completedTasks: PropTypes.array,
  tasksLoading: PropTypes.bool,
  tasksError: PropTypes.string,
  uploadDocument: PropTypes.func,
  deleteDocument: PropTypes.func,
  onTasksChange: PropTypes.func
};

export default observer(OfferOnboardingTasks);
